import { queryClient } from '@/lib/queryClient';
import { useConversationStore } from '@/stores/conversationStore';
import { useUserStore } from '@/stores/userStore';

/**
 * localStorage keys holding user-scoped client state that must NOT survive an
 * account switch. These are the persisted zustand stores plus the react-query
 * conversations snapshot (the persister key from main.tsx). If any of these
 * survive a logout, the next account hydrates the previous account's data.
 */
export const USER_SCOPED_PERSIST_KEYS = [
  'conversation-store', // project / conversation list — HeroScreen renders this directly
  'user-store', // activeTeam — keys the conversation fetch, so a stale value re-fetches the OTHER account's data
  'ivy-rq-cache', // persisted react-query conversations snapshot (main.tsx persister)
  'video-workspace-storage', // per-project slides / outline
  'message-store', // chat messages
  'impersonation-store', // admin "view as" — stale value would leak X-Impersonate-User onto the new session
];

/** localStorage marker for the last authenticated Firebase uid (account-switch detection). */
export const LAST_AUTH_UID_KEY = 'lastAuthUid';

/**
 * True when we've switched to a different account and must purge the previous
 * one's cached state. First-ever login (no prior uid) is NOT a switch, so we
 * never wipe a fresh login's freshly-loaded data.
 */
export function shouldResetForUidChange(lastUid: string | null, currentUid: string): boolean {
  return !!lastUid && lastUid !== currentUid;
}

/**
 * Wipe all user-scoped client state so a different account never sees the
 * previous account's projects/team. Clears BOTH the in-memory read-models (an
 * SPA logout->login does not reload the page) AND the persisted snapshots (so a
 * reload can't re-hydrate stale data). Auth tokens are cleared separately by
 * signOutUser().
 */
export function resetUserScopedClientState(): void {
  // In-memory react-query cache (team-keyed conversation list + counts).
  queryClient.clear();

  // In-memory zustand read-models that drive the dashboard.
  useConversationStore.getState().setConversations([]);
  useConversationStore.getState().setActiveConversation(null);
  useUserStore.getState().clearUserData();

  // Persisted snapshots — remove so the next hydrate starts clean.
  for (const key of USER_SCOPED_PERSIST_KEYS) {
    try {
      localStorage.removeItem(key);
    } catch {
      // ignore storage access errors (private mode / quota)
    }
  }
}
